<script setup lang="ts">
import { computed } from 'vue';
import router from '@/common/router';
import type { Message } from '@/ai/utils/types';
import { formatMessageTime, formatMessageTimeFull } from '@/ai/utils/utils';
import { useInternational } from '@/common/locale';

const { t } = useInternational();

interface Props {
  message: Message;
}

const props = defineProps<Props>();

/** 메시지 보낸 시각 (HH:mm) + 툴팁(전체 일시). */
const messageTime = computed(() => formatMessageTime(props.message.createdAt));
const messageTimeFull = computed(() => formatMessageTimeFull(props.message.createdAt));

/** 차트 썸네일 클릭 → 출처 화면으로 SPA 이동. AnalysisButton 으로 생성된 메시지에만 적용. */
const onClickThumbnail = () => {
  const path = props.message.sourcePath;
  if (!path) return;
  if (router.currentRoute.value.fullPath === path) return;
  router.push(path).catch(() => {
    /* duplicate / interrupted — 무시 */
  });
};
</script>

<template>
  <div class="user-message">
    <!--
      AnalysisButton 으로 생성된 메시지에는 imageUrl(차트 캡쳐 dataURL)과 sourcePath 가 포함됨.
      썸네일을 사용자 메시지 위에 작게 표시하고, 클릭 시 해당 차트가 있는 화면으로 이동.
    -->
    <button
      v-if="message.imageUrl"
      type="button"
      class="user-message__thumb"
      :title="
        t('WORD.CHATBOT.MESSAGE.GOTO_CHART', { title: message.sourceTitle || t('WORD.CHART') })
      "
      @click="onClickThumbnail"
      @mousedown.stop
    >
      <img
        :src="message.imageUrl"
        alt="captured chart"
        class="user-message__thumb-img"
      />
      <span
        v-if="message.sourceTitle"
        class="user-message__thumb-caption"
      >
        <i class="user-message__thumb-link-icon" />
        {{ message.sourceTitle }}
      </span>
    </button>
    <div
      class="user-message__text"
      @mousedown.stop
    >
      {{ message.message }}
    </div>
    <span
      v-if="messageTime"
      class="user-message__time"
      :title="messageTimeFull"
    >
      {{ messageTime }}
    </span>
  </div>
</template>

<style scoped lang="scss">
.user-message {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;

  &__text {
    max-width: 88%;
    padding: 10px 14px;
    border-radius: 14px 14px 4px;
    background: linear-gradient(135deg, #2563eb 0%, #3c8eff 60%, #6c4fff 100%);
    box-shadow: 0 8px 22px -6px rgb(60, 142, 255, 0.45);
    font-size: 13px;
    font-weight: 500;
    line-height: 1.5;
    color: #ffffff;
    text-align: left;
    overflow-wrap: break-word;
    white-space: pre-wrap;
  }

  /* 보낸 시각 — 풍선 아래 우측, 흐린 캡션. */
  &__time {
    padding: 0 4px;
    user-select: none;
    font-size: 10.5px;
    font-weight: 400;
    line-height: 14px;
    color: var(--color-gray-04-11, #98a0a8);
    font-variant-numeric: tabular-nums;
  }

  &__thumb {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    max-width: 88%;
    padding: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid rgb(60, 142, 255, 0.35);
    border-radius: 10px;
    background: rgb(255, 255, 255, 0.04);
    box-shadow: 0 4px 12px -4px rgb(0, 0, 0, 0.18);
    transition:
      border-color 0.15s ease,
      transform 0.15s ease,
      box-shadow 0.15s ease;

    &:hover {
      border-color: #3c8eff;
      box-shadow: 0 6px 18px -4px rgb(60, 142, 255, 0.35);
      transform: translateY(-1px);
    }
  }

  &__thumb-img {
    display: block;
    width: 100%;
    max-height: 140px;
    border-radius: 6px;
    background-color: #ffffff;
    object-fit: contain;
  }

  &__thumb-caption {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 6px 4px 2px;
    overflow: hidden;
    font-size: 11px;
    font-weight: 500;
    line-height: 14px;
    color: var(--color-gray-02-10, #d0d4d9);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__thumb-link-icon {
    flex-shrink: 0;
    width: 11px;
    height: 11px;
    background-color: currentcolor;

    /* simple inline 'external-link' SVG via mask */
    -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='black'><path d='M9 2v1.5h2.94L6.72 8.72l1.06 1.06L13 4.56V7.5h1.5V2H9zM3 4v9h9V8h-1.5v3.5h-6v-6H8V4H3z'/></svg>")
      center / contain no-repeat;
    mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='black'><path d='M9 2v1.5h2.94L6.72 8.72l1.06 1.06L13 4.56V7.5h1.5V2H9zM3 4v9h9V8h-1.5v3.5h-6v-6H8V4H3z'/></svg>")
      center / contain no-repeat;
  }

  /* legacy class names kept for backwards compat */
  &__image-wrapper {
    display: flex;
    justify-content: flex-end;
  }
  &__image {
    max-width: 100%;
    border-radius: 12px;
    background-color: white;
  }
}
</style>
